import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, ShieldAlert } from "lucide-react";
import { getMasterCampaign, patchMasterCampaign } from "@/lib/chiroApi";

const FIELDS = [
    {
        key: "tone",
        label: "Tone",
        helper: "How every AI draft should sound. Inherited by every campaign.",
        rows: 3,
    },
    {
        key: "claim_safety_rules",
        label: "Claim safety rules",
        helper: "Required hedged language + forbidden phrases.",
        rows: 8,
    },
    {
        key: "approval_policy",
        label: "Approval policy",
        helper: "How outreach is allowed to leave the system.",
        rows: 4,
    },
    {
        key: "receipt_standards",
        label: "Receipt standards",
        helper: "What we keep on the record per prospect.",
        rows: 5,
    },
    {
        key: "no_polsia_standards",
        label: "No-fake-numbers standards",
        helper: "What the system MUST NOT do, ever.",
        rows: 4,
    },
];

export default function MasterCampaign() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        getMasterCampaign().then(setData);
    }, []);

    const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const save = async () => {
        if (!data) return;
        setSaving(true);
        try {
            const body = {};
            for (const f of FIELDS) body[f.key] = data[f.key] ?? "";
            const next = await patchMasterCampaign(body);
            setData(next);
            toast.success("Master campaign saved");
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!data) {
        return (
            <div className="font-mono text-[12px] text-[#9ca3af]" data-testid="master-campaign-loading">
                Loading master campaign…
            </div>
        );
    }

    return (
        <div data-testid="master-campaign-page" className="max-w-4xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7cc93c] mb-2">
                // master campaign · global rules
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">Master Campaign</h1>
            <p className="text-[#9ca3af] text-sm mb-6 max-w-2xl">
                Global tone, claim-safety, approval policy, and receipt standards. Every Vertical Tracker
                and every Campaign inherits these rules. Editing here changes the rules every AI draft is
                graded against.
            </p>

            <div
                className="mb-6 p-4 border border-[#ff9f0a]/40 bg-[#ff9f0a]/5 rounded-[2px] flex items-start gap-3"
                data-testid="master-campaign-autosend-banner"
            >
                <ShieldAlert className="w-5 h-5 text-[#ff9f0a] shrink-0 mt-0.5" />
                <div className="text-sm">
                    <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#ff9f0a] mb-1">
                        auto-send: disabled
                    </div>
                    <div className="text-[#d1d5db]">
                        ProofCommand never sends outreach automatically. Every drafted email must be
                        explicitly approved by an admin and copied into Gmail by a human.
                    </div>
                </div>
            </div>

            <div className="space-y-5">
                {FIELDS.map((f) => (
                    <div key={f.key} data-testid={`mc-field-${f.key}`}>
                        <label className="block font-mono text-[11px] tracking-[0.2em] uppercase text-[#9ca3af] mb-1">
                            {f.label}
                        </label>
                        <div className="text-[11px] text-[#6b7280] mb-2">{f.helper}</div>
                        <textarea
                            value={data[f.key] ?? ""}
                            onChange={(e) => update(f.key, e.target.value)}
                            rows={f.rows}
                            data-testid={`mc-textarea-${f.key}`}
                            className="w-full bg-[#0f1113] border border-[#262a2e] rounded-[2px] p-3 font-mono text-[12px] text-[#f3f4f6] focus:outline-none focus:border-[#7cc93c]"
                        />
                    </div>
                ))}
            </div>

            <div className="mt-6 flex items-center gap-3">
                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="mc-save-btn"
                    className="flex items-center gap-2 bg-[#7cc93c] text-[#090a0b] hover:bg-[#9be05c] disabled:opacity-50 font-mono text-[12px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-[2px]"
                >
                    <Save className="w-3.5 h-3.5" />
                    {saving ? "Saving…" : "Save master campaign"}
                </button>
                {data.updated_at && (
                    <span className="font-mono text-[11px] text-[#6b7280]">
                        last updated {new Date(data.updated_at).toLocaleString()}
                    </span>
                )}
            </div>
        </div>
    );
}
